/**
 * /zh/user-story-template — 用户故事模板中文 SEO 页。与 UserStoryTemplate.tsx
 * 同构，原生中文，hreflang 双向。
 */
import { MarketingShell } from './MarketingShell';

export function UserStoryTemplateZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/user-story-template"
      documentTitle="用户故事模板（作为…我想…以便…）· Draftlize"
      documentDescription="一份可直接抄的用户故事模板——「作为…我想…以便…」格式、INVEST 清单、验收标准——并讲清怎么让故事连着它底下的决策。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">模板 · 用户故事</span>
          <h1 className="serif" style={{ fontSize: 'clamp(38px, 4.8vw, 58px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            一份用户故事模板，<br/>
            <em>始终连着那个「为什么」。</em>
          </h1>
          <p className="lede">
            你为一份<em>用户故事模板</em>而来——「作为…我想…以便…」格式、INVEST 清单、验收标准那一块。它在下面；抄进 Jira、文档、或任何放故事的地方。但要知道它的翻车方式：一个故事用一句话捕住需求，随即就和那个为它正名的产品决策脱了钩。当那个决策变了，故事还赖在 sprint 里，悄悄地造错的东西。Draftlize 给你同样的格式——并让每个故事连到它所依赖的决策。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">模板</span>
            <h2 className="serif">这个格式。<em>随处可抄。</em></h2>
          </div>
          <p>一个用户故事是分三部分的一句话，加上让它可测试的标准。这就是整份模板——敏捷团队一直用的那个形状。</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: '作为 [角色]', hint: '谁', body: '具体的用户或人物，而不是「用户」。「作为一个在评审路线图的 PM」框定了需求；「作为用户」什么都没框定。角色是让故事对「它服务于谁」保持诚实的东西。' },
            { k: '我想 [能力]', hint: '什么', body: '他们想做的事，表达成一种能力，而不是一个 UI。「我想从 spec 里引用一个决策」——而不是「我想要一个下拉框」。长什么样是设计决策；故事讲的是目标。' },
            { k: '以便 [收益]', hint: '为什么', body: '让它值得做的那个结果。这是团队最爱省、却最重要的部分——它是「这故事到底值不值得做」的检验，也是范围被质疑时第一个要核对的东西。' },
            { k: '验收标准', hint: '何时算完成', body: '让它可上线的条件，最好写成 Given/When/Then。这是故事变得可测试的地方。完整范式见专门的验收标准指南。' },
            { k: 'INVEST 自检', hint: '质量线', body: '一个好故事是独立、可协商、有价值、可估算、够小、可测试的。把草稿故事对照这六条，弱的那些（太大、不可测、没价值）会在进 sprint 之前掉队。' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 24, padding: '20px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <div>
                <span className="serif" style={{ fontSize: 18, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.3 }}>{f.k}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 700 }}>{f.hint}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.75, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: '.04em', color: 'var(--ink-3)', textAlign: 'center', marginTop: 18 }}>
          走一个例子：<em>作为一个在评审路线图的 PM，我想从 spec 里引用一个决策，以便我不必每个 sprint 都重新解释一遍。</em>
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">故事为什么<em>会飘走。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">「以便」是复制，不是链接</h3>
            <p>收益从句引用了一个产品决策——「以便」某个目标被服务。但在一张工单里它只是文字，是对一个住在别处的决策的复述。故事和它依赖的决策，是两个碰巧此刻一致、却互不相连的东西。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">决策变了，故事没变</h3>
            <p>故事背后的目标变了——你重切范围、重排优先级、砍掉一个人群。故事还赖在 backlog 里，带着它原来的「以便」，指向一个已不成立的理由。没人会在决策一动时重读每一个故事，于是 sprint 悄悄朝着一个过时的「为什么」开工。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">验收标准与 spec 脱节</h3>
            <p>标准是对着一份后来变了的 spec 写的。故事说「完成于 X」，spec 现在说 Y，缝隙在评审或 QA 时才浮现——晚、贵，而本可避免，只要标准当初是连到 spec、而不是从它复制来的。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">同一个故事，<em>接上线。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">每个故事是连着「为什么」的卡片</h3>
            <p>在 Draftlize 里，故事不是孤立的工单——它是一张卡片，其「以便」连到它所服务的那张真实决策卡。收益不被复述，而被引用。打开故事，你能直接走到为它正名的那个决策。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">决策一变，故事标 stale</h3>
            <p>因为链接是真的，改了故事背后的决策，故事自动变 stale——就像构建系统让被改文件的下游全部失效一样。你在一个 sprint 照旧把它们造出来之前，就看到哪些故事刚失去了它们的理由。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">你的 agent 读故事，也读它的上下文</h3>
            <p>当 Claude Code 或 Cursor 经 MCP 拿起一个故事，它也读到相连的决策和验收标准——于是它对着当前的意图实现，而不是一句三个决策之前就失去意义的话。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          用户故事用一句话捕住需求，却说不出那个让需求成立的决策何时已经走远。
        </blockquote>
        <cite>留着这个格式，让「以便」链接到决策，而不只是描述它。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '标准的用户故事格式是什么？', a: '经典模板是「作为 [角色]，我想 [能力]，以便 [收益]」。角色点名它给谁，能力点名目标（而不是 UI），收益点名它为什么值得做。再加上验收标准，让它可测试。' },
            { q: '用户故事和用例有什么区别？', a: '用户故事是从用户视角出发、简短而非正式的需求陈述，意在开启一场对话；用例是对用户与系统之间交互步骤更详细的描述。故事轻量、敏捷，用例更重、更完整。' },
            { q: '什么是好的用户故事？（INVEST）', a: '好故事是独立、可协商、有价值、可估算、够小、可测试的。最常见的失败是：大到无法估算、「以便」里没有清晰价值、或因为没有验收标准而不可测试。' },
            { q: '用户故事和验收标准有什么区别？', a: '故事陈述需求；验收标准陈述「在什么条件下算完成」，通常写成 Given/When/Then。故事是一眼能看到的为什么与做什么，标准是对「成功」精确、可测试的定义。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">写不丢「为什么」的故事<em>——$5 免费起步。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>只为实际用量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            用上面的模板，或在 Draftlize 里把每个故事写成连着它所服务决策的卡片——这样决策一动，故事就标 stale，而不是悄悄造错东西。
          </p>
          <a className="btn-primary" href="/">$5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
